import { IconButton } from "@dropbox/dig-components/buttons";
import { UIIcon } from "@dropbox/dig-icons";
import { ArrowDownLine } from "@dropbox/dig-icons/assets";
import { PAP_Click_MessageAction } from "@mirage/analytics/events/types/click_message_action";
import type { MarkdownResponseProps } from "@mirage/mosaics/Chat/components/chat/markdown/MarkdownResponse";
import { useClipboardCopyListenerOnRef } from "@mirage/shared/hooks/useClipboardCopyListenerOnRef";
import { usePrevious } from "@mirage/shared/util/hooks";
import i18n from "@mirage/translations";
import classNames from "classnames";
import React, {
  Fragment,
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { ConversationMessageLoadingText } from "../ConversationMessageLoadingText";
import styles from "./ConversationMessages.module.css";

import type { PAPEvent } from "@mirage/analytics/events/base/event";
import type { ActionSurfaceComponent } from "@mirage/analytics/events/enums/action_surface_component";
import { ConversationMessageRow } from "@mirage/mosaics/Chat/components/chat/ConversationMessages/ConversationMessageRow";
import type { ToolCallsRunner } from "@mirage/mosaics/Chat/components/chat/ConversationMessages/ToolCalls/ToolCallsRunner";
import type {
  LayoutVariant,
  RenderMessageActions,
} from "@mirage/mosaics/Chat/components/chat/ConversationMessages/types";
import { getProgressStepState } from "@mirage/mosaics/Chat/components/chat/ConversationMessages/useProgressHandling";
import { shouldShowSearchResultsInChat } from "@mirage/mosaics/Chat/utils/searchInChat";
import { getSearchResultsPlacementForChat } from "@mirage/mosaics/SearchPageDataRetrieval/searchPageDataRetrieval";
import type { ConversationType } from "@mirage/service-conversation/router_state_types";
import type { UserMessageOptions } from "@mirage/service-conversation/types";
import { useFeatureFlagValue } from "@mirage/service-experimentation/useFeatureFlagValue";
import { convertFeatureValueToBool } from "@mirage/service-experimentation/util";
import type { ComposeArtifact } from "@mirage/shared/compose/compose-session";
import useSearchQueryId from "@mirage/shared/hooks/useSearchQueryId";
import { isEmbedPage } from "@mirage/shared/util/tiny-utils";
import type {
  ChatConversationMessage,
  ChatConversationMessageMessage,
  ChatSource,
} from "../../../types";

interface ConversationMessagesProps {
  variant: LayoutVariant;
  noPadding?: boolean;
  startingMessageNode?: React.ReactNode;
  messages: ChatConversationMessage[];
  isWaitingForResponse: boolean;
  isGeneratingResponse: boolean;
  isChatTrialNotificationVisible: boolean;
  progressComponent: React.ReactNode | undefined;
  onRemoveSource: (source: ChatSource) => void;
  isLastMessageFollowUpSuggestions: boolean;
  artifacts: ComposeArtifact[];
  logComposeEvent: (
    event: PAPEvent,
    overrides?: { actionSurfaceComponent?: ActionSurfaceComponent },
  ) => void;
  onOpenArtifact: () => void;
  /**
   * Render a list of new sources that have been added since the last message.
   */
  renderNewSources?: () => React.ReactNode;
  renderMessageActions?: RenderMessageActions;
  hideFirstUserMessage?: boolean;
  hideSources?: boolean; // hide sources in the message rows, default to false
  showStartingNodeAfterContextInputs?: boolean;
  /**
   * Render a footer for the conversation -- this will be displayed at the bottom of the
   * scrollable messages area, below the last message.
   */
  footer?: React.ReactNode;
  fullHeight?: boolean;
  conversationId?: string;
  searchSessionId?: string;
  assistRequestId?: string;
  spanId?: string;
  /**
   * Height of the conversation input component used for positioning the scroll indicator
   */
  conversationInputHeight?: number;
  userMessageOptions?: UserMessageOptions;
  overrideMarkdownRenderer?: (props: MarkdownResponseProps) => React.ReactNode;
  conversationType?: ConversationType;
  showDraftGeneratedButton?: boolean;
  toolCallsRunner?: ToolCallsRunner;
}

export const ConversationMessages = memo(
  ({
    variant,
    noPadding,
    startingMessageNode,
    messages,
    isWaitingForResponse,
    isGeneratingResponse,
    isChatTrialNotificationVisible,
    progressComponent,
    onRemoveSource,
    artifacts,
    logComposeEvent,
    onOpenArtifact,
    renderNewSources,
    renderMessageActions,
    hideSources = false,
    hideFirstUserMessage = false,
    footer,
    fullHeight = false,
    conversationId,
    searchSessionId,
    assistRequestId,
    spanId,
    conversationInputHeight,
    userMessageOptions,
    overrideMarkdownRenderer,
    conversationType,
    showDraftGeneratedButton,
    toolCallsRunner,
  }: ConversationMessagesProps) => {
    // Scroll to bottom when messages hydrate
    const messagesContainerRef = useRef<HTMLDivElement>(null);
    const messagesScrollerRef = useRef<HTMLDivElement>(null);
    const previousMessagesLengthRef = useRef(0);
    const [showTemporaryPadding, setShowTemporaryPadding] = useState(false);
    const { searchQueryUuid } = useSearchQueryId();
    const isEmbed = isEmbedPage();

    const showSearchResultsInChat = convertFeatureValueToBool(
      useFeatureFlagValue("dash_2025_06_10_search_in_chat"),
    );

    const searchResultsCache = useRef<
      Map<string, Promise<"hidden" | "top" | "bottom">>
    >(new Map());

    // The most recent user message in the conversation (regardless of position)
    const actualLastUserMessage = useMemo(() => {
      for (let i = messages.length - 1; i >= 0; i--) {
        if (messages[i].role === "user") {
          return messages[i] as ChatConversationMessage;
        }
      }
      return undefined;
    }, [messages]);

    useEffect(() => {
      if (
        !shouldShowSearchResultsInChat(
          actualLastUserMessage,
          showSearchResultsInChat,
          userMessageOptions,
          conversationType,
        )
      ) {
        return;
      }

      const query =
        actualLastUserMessage?.type === "message"
          ? actualLastUserMessage.text
          : undefined;
      if (!query) return;

      if (!searchResultsCache.current.has(query)) {
        searchResultsCache.current.set(
          query,
          getSearchResultsPlacementForChat({
            query,
          }),
        );
      }
    }, [
      actualLastUserMessage,
      showSearchResultsInChat,
      userMessageOptions?.searchParams?.searchTarget?.case,
      conversationType,
    ]);

    // If the last message happens to be from a user
    // Detect when a new user message is added and scroll it to top
    const lastMessage = messages[messages.length - 1];
    const lastUserMessage = lastMessage?.role === "user" ? lastMessage : null;
    // Track changes to lastUserMessage for scroll behavior
    const previousLastUserMessage = usePrevious(lastUserMessage);
    const hasAssistantMessages = messages.some((m) => m.role === "assistant");
    useEffect(() => {
      const isNewUserMessage =
        lastUserMessage &&
        lastUserMessage !== previousLastUserMessage &&
        messages.length > previousMessagesLengthRef.current;
      // Only apply temporary padding and auto-scroll if there's already been at least one assistant response
      if (isNewUserMessage && hasAssistantMessages) {
        setShowTemporaryPadding(true);
        // Wait for DOM update, then scroll
        setTimeout(() => {
          if (messagesScrollerRef.current) {
            const userMessages =
              messagesScrollerRef.current.querySelectorAll(
                '[data-role="user"]',
              );
            const lastUserMessage = userMessages[
              userMessages.length - 1
            ] as HTMLElement;
            if (lastUserMessage) {
              const scrollerRect =
                messagesScrollerRef.current.getBoundingClientRect();
              const messageRect = lastUserMessage.getBoundingClientRect();
              const scrollTop = messagesScrollerRef.current.scrollTop;
              const targetScrollTop =
                scrollTop + messageRect.top - scrollerRect.top - 20;
              messagesScrollerRef.current.scrollTo({
                top: targetScrollTop,
                behavior: "smooth",
              });
            }
          }
        }, 50);
      }
      previousMessagesLengthRef.current = messages.length;
    }, [
      messages,
      lastUserMessage,
      previousLastUserMessage,
      hasAssistantMessages,
    ]);

    // Wait 1 second after response completes to allow streaming to start before removing padding
    useEffect(() => {
      if (!isWaitingForResponse && showTemporaryPadding) {
        const timer = setTimeout(() => {
          setShowTemporaryPadding(false);
        }, 1000);
        return () => clearTimeout(timer);
      }
    }, [isWaitingForResponse, showTemporaryPadding]);

    // Scroll to bottom on initial load or when messages first appear
    useLayoutEffect(() => {
      if (messages.length > 0 && previousMessagesLengthRef.current === 0) {
        if (messagesScrollerRef.current) {
          messagesScrollerRef.current.scrollTo(
            0,
            messagesScrollerRef.current.scrollHeight,
          );
        }
      }
    }, [messages]);

    // Scroll indicator state
    const isScrollArrowEnabled = convertFeatureValueToBool(
      useFeatureFlagValue("dash_2025_04_23_conversation_scroll_arrow"),
    );
    const [showScrollButton, setShowScrollButton] = useState(false);

    // Check if content is scrollable and update button visibility
    const checkScrollability = useCallback(() => {
      if (!messagesScrollerRef.current) return;
      const { scrollTop, scrollHeight, clientHeight } =
        messagesScrollerRef.current;
      const isNearBottom = scrollHeight - scrollTop - clientHeight < 30;
      const isScrollable = scrollHeight > clientHeight;
      setShowScrollButton(
        !showTemporaryPadding &&
          !isChatTrialNotificationVisible &&
          (!isNearBottom || (isScrollable && scrollTop === 0)),
      );
    }, [showTemporaryPadding, isChatTrialNotificationVisible]);

    // Handle scroll events
    const handleScroll = useCallback(() => {
      checkScrollability();
    }, [checkScrollability]);

    // Add scroll event listener
    useEffect(() => {
      const messagesDiv = messagesScrollerRef.current;
      if (messagesDiv) {
        messagesDiv.addEventListener("scroll", handleScroll);
        handleScroll(); // Trigger initial check
        return () => messagesDiv.removeEventListener("scroll", handleScroll);
      }
    }, [handleScroll]);

    // Add ResizeObserver to detect content height changes during streaming
    useEffect(() => {
      const messagesDiv = messagesContainerRef.current;
      if (!messagesDiv) return;
      const resizeObserver = new ResizeObserver(() => {
        checkScrollability();
      });
      resizeObserver.observe(messagesDiv);
      return () => {
        resizeObserver.disconnect();
      };
    }, [checkScrollability]);

    useEffect(() => {
      checkScrollability();
    }, [isWaitingForResponse, checkScrollability]);

    const scrollToBottom = useCallback(() => {
      if (messagesScrollerRef.current) {
        messagesScrollerRef.current.scrollTo({
          top: messagesScrollerRef.current.scrollHeight,
          behavior: "smooth",
        });
      }
    }, []);

    const handleCopyListener = useCallback(
      (selection: string) => {
        logComposeEvent(
          PAP_Click_MessageAction({
            actionType: "copy",
            generatedQueryString: selection,
            queryLength: selection.length,
            entryPoint: "keyboard",
            searchSessionId,
            searchQueryUuid,
            conversationId,
            assistRequestId,
          }),
        );
      },
      [logComposeEvent],
    );
    const attachCopyListener =
      useClipboardCopyListenerOnRef(handleCopyListener);
    useEffect(() => {
      if (messagesScrollerRef.current) {
        return attachCopyListener(messagesScrollerRef.current);
      }
    }, [attachCopyListener]);
    const hasDraftArtifact = artifacts.some(
      (artifact) => artifact.type === "markdown_draft",
    );
    let displayMessages =
      hideFirstUserMessage && messages.length > 0
        ? messages.slice(messages[0].role === "user" ? 1 : 0)
        : messages;

    const assistantMessages = displayMessages.filter(
      (m): m is ChatConversationMessageMessage =>
        m.role === "assistant" && m.type === "message",
    );

    const lastAssistantMsgIsAnalysis =
      assistantMessages.length > 0 &&
      assistantMessages[assistantMessages.length - 1].actionContext &&
      (assistantMessages[assistantMessages.length - 1].actionContext?.type ===
        "dash_search" ||
        assistantMessages[assistantMessages.length - 1].actionContext?.type ===
          "dash_search_analyze");

    // if lastAsisstantMsgIsAnalysis is true, filter out all messages that are not
    // dash_search or dash_search_analyze EXCEPT the last assistant message that is
    // dash_search and the last assistant message that is dash_search_analyze
    // else if lastAssistantMsgIsAnalysis is false, filter out all messages that are
    // dash_search or dash_search_analyze
    if (lastAssistantMsgIsAnalysis) {
      displayMessages = displayMessages.reduce<ChatConversationMessage[]>(
        (acc, msg, index) => {
          if (
            (msg as ChatConversationMessageMessage).actionContext?.type ===
              "dash_search" ||
            (msg as ChatConversationMessageMessage).actionContext?.type ===
              "dash_search_analyze"
          ) {
            if (
              index === displayMessages.length - 1 ||
              index === displayMessages.length - 2
            ) {
              acc.push(msg);
            }
          } else {
            acc.push(msg);
          }
          return acc;
        },
        [],
      );
    } else {
      displayMessages = displayMessages.filter(
        (msg): msg is ChatConversationMessageMessage =>
          (msg as ChatConversationMessageMessage).actionContext?.type !==
            "dash_search" &&
          (msg as ChatConversationMessageMessage).actionContext?.type !==
            "dash_search_analyze",
      );
    }

    // When the last message is an assistant message, show progress above so that the loading
    // text stays in the same place as the message streams in below. If the last message is a user
    // message then the progress message should be below the message
    const progressMessage = progressComponent || (
      <div
        className={classNames(
          styles.LoadingMessageRow,
          styles.ConversationMessageRowMessageAnimation,
        )}
      >
        <ConversationMessageLoadingText
          size={variant === "condensed" || isEmbed ? "medium" : "large"}
          text={
            hasDraftArtifact
              ? i18n.t("compose_message_waiting_for_response_drafting")
              : i18n.t("compose_message_waiting_for_response")
          }
        />
      </div>
    );
    return (
      <div
        className={classNames(styles.ConversationMessagesScroller, {
          [styles.ConversationMessagesScrollerCondensed]:
            variant === "condensed",
          [styles.ConversationMessagesScrollerNoPadding]: noPadding,
          [styles.ConversationMessagesScrollerFullHeight]: fullHeight,
        })}
        ref={messagesScrollerRef}
      >
        <div
          className={classNames(styles.ConversationMessages, {
            [styles.ConversationMessagesScrollerFullHeight]: fullHeight,
            [styles.ConversationMessagesWithTemporaryPadding]:
              showTemporaryPadding,
          })}
          role="log"
          aria-live="polite"
          ref={messagesContainerRef}
        >
          {startingMessageNode}
          {displayMessages.map((message, i) => (
            <Fragment key={i + message.role}>
              {isWaitingForResponse &&
                i === displayMessages.length - 1 &&
                message.role === "assistant" &&
                progressMessage}
              <ConversationMessageRow
                variant={variant}
                message={message}
                onRemoveSource={onRemoveSource}
                hideSources={hideSources}
                isLastMessage={
                  i === displayMessages.length - 1 && !isWaitingForResponse
                }
                progressStepState={getProgressStepState(
                  message,
                  displayMessages,
                  isWaitingForResponse,
                )}
                logComposeEvent={logComposeEvent}
                onOpenArtifact={onOpenArtifact}
                renderActions={renderMessageActions}
                conversationId={conversationId}
                searchSessionId={searchSessionId}
                assistRequestId={assistRequestId}
                spanId={spanId}
                lastUserMessage={
                  message.role === "assistant"
                    ? displayMessages[i - 1]
                    : undefined
                }
                searchResultsCache={searchResultsCache}
                overrideMarkdownRenderer={overrideMarkdownRenderer}
                showDraftGeneratedButton={showDraftGeneratedButton}
                isWaitingForResponse={isWaitingForResponse}
                isGeneratingResponse={isGeneratingResponse}
                toolCallsRunner={toolCallsRunner}
              />
              {isWaitingForResponse &&
                i === displayMessages.length - 1 &&
                message.role === "user" &&
                progressMessage}
            </Fragment>
          ))}
          {renderNewSources && renderNewSources()}
          {footer}
        </div>
        {showScrollButton && isScrollArrowEnabled && (
          <div
            className={styles.ScrollIndicator}
            style={
              conversationInputHeight
                ? {
                    bottom: `${conversationInputHeight + 45}px`,
                  }
                : undefined
            }
          >
            <IconButton
              variant="opacity"
              onClick={scrollToBottom}
              aria-label="Scroll to bottom"
              shape="standard"
            >
              <UIIcon src={ArrowDownLine} />
            </IconButton>
          </div>
        )}
      </div>
    );
  },
);
ConversationMessages.displayName = "ConversationMessages";
